import type { ReactElement } from "react";
import { Card, ChevronIcon, Text, Button } from "@olivahealth/oli-ui";
import { graphql, useFragment } from "react-relay";
import { formatCompactDate } from "../../../utils/date/format";
import useTranslation from "../../../hooks/useTranslation";
import { CompanionSessionCard_thread$key } from "./__generated__/CompanionSessionCard_thread.graphql";

// Re-export skeleton for external use
export { CompanionSessionCardSkeleton } from "./Loader";

interface Props {
  thread: CompanionSessionCard_thread$key;
  onClick: (id: string) => void;
  variant?: "default" | "compact";
}

const CompanionSessionCardFragment = graphql`
  fragment CompanionSessionCard_thread on Thread {
    id
    type
    createdAt
    title
    summary {
      body
      title
    }
  }
`;

export default function CompanionSessionCard({
  thread,
  onClick,
  variant = "default",
}: Props): ReactElement {
  const { t } = useTranslation("companion", {
    keyPrefix: "companionSessionCard",
  });

  const data = useFragment<CompanionSessionCard_thread$key>(
    CompanionSessionCardFragment,
    thread,
  );

  if (variant === "default") {
    return (
      <Card
        backgroundClassName="bg-neutral-800 hover:bg-neutral-700"
        border={false}
        padding="md"
        rounding="2xl"
        key={data.id}
        onClick={() => onClick(data.id)}
        onKeyDown={() => onClick(data.id)}
        fullHeight={false}
      >
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-col gap-y-1">
            <Text weight="medium" size="sm" color="light">
              {formatCompactDate(data.createdAt)}
            </Text>
            <Text weight="bold" size="base" color="white" gutter="xs">
              {data.title || t("fallbackTitle")}
            </Text>
            {data.summary?.title && (
              <Text lineClamp={3} color="white" size="sm">
                {data.summary.title}
              </Text>
            )}
          </div>
          <div className="flex justify-start mt-2">
            <Button
              variant="linkLight"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClick(data.id);
              }}
            >
              {t("viewMoreCta")}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      backgroundClassName="bg-neutral-800 hover:bg-neutral-700 py-3 pl-4 pr-2"
      border={false}
      rounding="2xl"
      key={data.id}
      onClick={() => onClick(data.id)}
      onKeyDown={() => onClick(data.id)}
      fullHeight={false}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-y-1">
          <Text weight="medium" size="sm" color="light">
            {formatCompactDate(data.createdAt)}
          </Text>
          <Text lineClamp={2} color="white">
            {data.title || t("fallbackTitle")}
          </Text>
        </div>

        <ChevronIcon direction="right" color="white" />
      </div>
    </Card>
  );
}
